import type { FC } from 'react';
import React, { useCallback } from 'react';
import Slider from 'react-slick';
import { Box } from '@withjoy/joykit';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { SliderContainer } from './PhotoSlider.styles';
import type { Photo, BrannanPhotoSliderProps } from './PhotoSlider.types';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { useSliderSettings } from './hooks/useSliderSettings';
import { PHOTO_COUNT_TO_SHOW, FULL_WIDTH_PROPERTIES } from './PhotoSlider.constants';
import { CONTAINER_ASPECT_RATIO } from './PhotoSlider.config';

export const BrannanPhotoSlider: FC<BrannanPhotoSliderProps> = props => {
  const { photos, showPlaceholder, aspectRatio = CONTAINER_ASPECT_RATIO, photoRenderer: PhotoRenderer, crossfadeDelayMs } = props;

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW, showPlaceholder && photos.length === 0);

  const [containerDimensions, containerRef] = useSizeObserver();
  const { settings, sliderRef } = useSliderSettings();

  const getPhotoComponent = useCallback(
    (photo: Photo) => (
      <Box
        key={photo.id}
        width="100%"
        aspectRatio={`${aspectRatio}`}
        height="auto"
      >
        <PhotoRenderer
          photoUrl={photo.photoUrl}
          cropInfo={photo.cropInfo ? { ...photo.cropInfo, unit: '%' } : null}
          width={containerDimensions.width}
          height={containerDimensions.width / aspectRatio}
          containerType="fixed-aspect"
          crossfadeDelayMs={crossfadeDelayMs}
        />
      </Box>
    ),
    [containerDimensions.width, aspectRatio, PhotoRenderer, crossfadeDelayMs],
  );

  return (
    <SliderContainer
      ref={containerRef}
      height="100%"
      position="relative"
      aspectRatio={filledPhotos.length > 0 ? `${aspectRatio}` : undefined}
      {...FULL_WIDTH_PROPERTIES}
    >
      <Slider
        ref={sliderRef}
        {...settings}
      >
        {filledPhotos.map((photo, index) => {
          return photo.photoUrl ? (
            getPhotoComponent(photo)
          ) : (
            <Box
              key={index}
              width="100%"
              height="auto"
              aspectRatio={`${aspectRatio}`}
              backgroundColor="lightgray"
            />
          );
        })}
      </Slider>
    </SliderContainer>
  );
};

BrannanPhotoSlider.displayName = 'BrannanPhotoSlider';
